import React, { useRef, useState } from 'react';
import { Upload, X, FileText, File, FileSpreadsheet, Loader2, Trash2, Eye } from 'lucide-react';
import { KnowledgeDocument } from '@/services/knowledge.service';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = '.pdf,.txt,.md,.docx,.doc,.csv,.html,.json,.xml';

interface FileUploadZoneProps {
  pendingFiles: File[];
  onFilesChange: (files: File[]) => void;
  existingDocuments?: (KnowledgeDocument & { knowledge_base_name?: string })[];
  onDeleteDocument?: (docId: string) => void;
  onViewDocument?: (doc: KnowledgeDocument) => void;
  isUploading?: boolean;
}

function getFileIcon(type: string) {
  const t = type?.toLowerCase() || '';
  if (t.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  if (t.includes('word') || t.includes('docx')) return <File className="w-5 h-5 text-blue-600" />;
  if (t.includes('csv') || t.includes('spreadsheet')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  if (t.includes('markdown') || t.includes('md')) return <FileText className="w-5 h-5 text-purple-500" />;
  if (t.includes('html')) return <FileText className="w-5 h-5 text-orange-500" />;
  return <FileText className="w-5 h-5 text-gray-400" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  pendingFiles,
  onFilesChange,
  existingDocuments = [],
  onDeleteDocument,
  onViewDocument,
  isUploading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.size <= MAX_FILE_SIZE);
    onFilesChange([...pendingFiles, ...files]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.size <= MAX_FILE_SIZE);
    onFilesChange([...pendingFiles, ...files]);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    onFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {/* Existing Documents (edit mode) */}
      {existingDocuments.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Uploaded Documents ({existingDocuments.length})
          </p>
          <div className="max-h-48 overflow-y-auto space-y-2">
            {existingDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border-2 border-[#1a1a2e]"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-1.5 bg-white rounded-md">
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                    <p className="text-xs text-gray-500">
                      {doc.file_type} {doc.file_size ? `- ${formatFileSize(doc.file_size)}` : ''}
                      {' '}<span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                        doc.status === 'ready' ? 'bg-green-100 text-green-700'
                        : doc.status === 'error' ? 'bg-red-100 text-red-700'
                        : (doc.status === 'extracting' || doc.status === 'embedding' || doc.status === 'graphing')
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>{doc.status}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {onViewDocument && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onViewDocument(doc); }}
                      className="p-1.5 hover:bg-blue-50 rounded-md transition-colors"
                      title="View document"
                    >
                      <Eye className="w-4 h-4 text-blue-500" />
                    </button>
                  )}
                  {onDeleteDocument && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDeleteDocument(doc.id); }}
                      className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete document"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isUploading
            ? 'border-[#1a1a2e] bg-gray-50 cursor-not-allowed'
            : isDragging
              ? 'border-blue-500 bg-blue-50 cursor-pointer'
              : 'border-[#1a1a2e] hover:border-blue-400 cursor-pointer'
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
            <p className="text-sm font-medium text-gray-700">Uploading files...</p>
          </>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            <p className="text-sm font-medium text-gray-700">
              {isDragging ? 'Drop files here' : 'Drop files here or click to browse'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, TXT, MD, DOCX, CSV, HTML, JSON, XML (max 5MB per file)
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading}
        />
      </div>

      {/* Pending Files */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Files to Upload ({pendingFiles.length})
          </p>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {pendingFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border-2 border-[#1a1a2e]"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-1.5 bg-white rounded-md">
                    {getFileIcon(file.name.split('.').pop() || '')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removePendingFile(index)}
                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                  disabled={isUploading}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
